import React from 'react';
import { format } from 'date-fns';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BeforeAfterComparison = ({ photos, selectedPhotos, setSelectedPhotos }) => {
  const handleSelectPhoto = (photo, position) => {
    setSelectedPhotos(prev => ({ ...prev, [position]: photo }));
  };

  const calculateDifference = () => {
    if (!selectedPhotos?.before || !selectedPhotos?.after) return null;

    const weightDiff = selectedPhotos?.after?.weight && selectedPhotos?.before?.weight
      ? (selectedPhotos?.after?.weight - selectedPhotos?.before?.weight)?.toFixed(1)
      : null;

    const bodyFatDiff = selectedPhotos?.after?.bodyFatPercentage && selectedPhotos?.before?.bodyFatPercentage
      ? (selectedPhotos?.after?.bodyFatPercentage - selectedPhotos?.before?.bodyFatPercentage)?.toFixed(1)
      : null;

    return { weightDiff, bodyFatDiff };
  };

  const differences = calculateDifference();

  return (
    <div className="space-y-6">
      {/* Comparison View */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Before Photo */}
        <div className="bg-card border border-border rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">Before</h3>
            {selectedPhotos?.before && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedPhotos(prev => ({ ...prev, before: null }))}
              >
                <Icon name="X" size={16} />
              </Button>
            )}
          </div>

          {selectedPhotos?.before ? (
            <div>
              <div className="aspect-square rounded-lg overflow-hidden mb-4">
                <img
                  src={selectedPhotos?.before?.signedUrl}
                  alt="Before photo"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  {format(new Date(selectedPhotos?.before?.takenAt), 'MMMM dd, yyyy')}
                </p>
                {selectedPhotos?.before?.weight && (
                  <div className="flex items-center gap-2">
                    <Icon name="Weight" size={16} className="text-muted-foreground" />
                    <span className="text-sm text-foreground">{selectedPhotos?.before?.weight} lbs</span>
                  </div>
                )}
                {selectedPhotos?.before?.bodyFatPercentage && (
                  <div className="flex items-center gap-2">
                    <Icon name="Percent" size={16} className="text-muted-foreground" />
                    <span className="text-sm text-foreground">{selectedPhotos?.before?.bodyFatPercentage}% BF</span>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="aspect-square rounded-lg border-2 border-dashed border-border flex items-center justify-center">
              <div className="text-center">
                <Icon name="Image" size={48} className="mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">Select a before photo</p>
              </div>
            </div>
          )}
        </div>

        {/* After Photo */}
        <div className="bg-card border border-border rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">After</h3>
            {selectedPhotos?.after && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedPhotos(prev => ({ ...prev, after: null }))}
              >
                <Icon name="X" size={16} />
              </Button>
            )}
          </div>

          {selectedPhotos?.after ? (
            <div>
              <div className="aspect-square rounded-lg overflow-hidden mb-4">
                <img
                  src={selectedPhotos?.after?.signedUrl}
                  alt="After photo"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  {format(new Date(selectedPhotos?.after?.takenAt), 'MMMM dd, yyyy')}
                </p>
                {selectedPhotos?.after?.weight && (
                  <div className="flex items-center gap-2">
                    <Icon name="Weight" size={16} className="text-muted-foreground" />
                    <span className="text-sm text-foreground">{selectedPhotos?.after?.weight} lbs</span>
                  </div>
                )}
                {selectedPhotos?.after?.bodyFatPercentage && (
                  <div className="flex items-center gap-2">
                    <Icon name="Percent" size={16} className="text-muted-foreground" />
                    <span className="text-sm text-foreground">{selectedPhotos?.after?.bodyFatPercentage}% BF</span>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="aspect-square rounded-lg border-2 border-dashed border-border flex items-center justify-center">
              <div className="text-center">
                <Icon name="Image" size={48} className="mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">Select an after photo</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Comparison Stats */}
      {differences && (
        <div className="bg-card border border-border rounded-xl p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Progress Summary</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {differences?.weightDiff && (
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Icon name="TrendingDown" size={24} className="text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Weight Change</p>
                  <p className="text-2xl font-bold text-foreground">
                    {differences?.weightDiff > 0 ? '+' : ''}{differences?.weightDiff} lbs
                  </p>
                </div>
              </div>
            )}
            {differences?.bodyFatDiff && (
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Icon name="Percent" size={24} className="text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Body Fat Change</p>
                  <p className="text-2xl font-bold text-foreground">
                    {differences?.bodyFatDiff > 0 ? '+' : ''}{differences?.bodyFatDiff}%
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Photo Selection Grid */}
      <div className="bg-card border border-border rounded-xl p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Select Photos to Compare</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {photos?.map((photo) => (
            <button
              key={photo?.id}
              onClick={() => {
                if (!selectedPhotos?.before) {
                  handleSelectPhoto(photo, 'before');
                } else if (!selectedPhotos?.after) {
                  handleSelectPhoto(photo, 'after');
                }
              }}
              className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                selectedPhotos?.before?.id === photo?.id || selectedPhotos?.after?.id === photo?.id
                  ? 'border-primary ring-2 ring-primary/20' :'border-border hover:border-primary/50'
              }`}
            >
              <img
                src={photo?.signedUrl}
                alt={format(new Date(photo?.takenAt), 'MMM dd')}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
        {photos?.length === 0 && (
          <div className="text-center py-12">
            <Icon name="Image" size={48} className="mx-auto text-muted-foreground mb-2" />
            <p className="text-muted-foreground">No photos available for comparison</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BeforeAfterComparison;